import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrencySigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import { BiomassOflAbcChart } from '../FisheryCharts/FisheryCharts';
import InsightColumnPair from '../InsightColumnPair';
import './TradeDeficit.css';

interface InsightData {
  tradeDeficit?: number;
  tradeDeficitUncertainty?: number;
}

interface TradeDeficitData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  onOpenOflAbcModal?: () => void;
}

const DESCRIPTION =
  'The net economic impact of domestic fish production on seafood imports and exports—specifically, how much your domestic landings help reduce the need for imported seafood. <i>Landed weight</i> × <i>imported price per unit</i>';

function filterBiomassByYear(
  biomass: unknown[] | undefined,
  selectedYear: string
): unknown[] {
  if (!biomass || !Array.isArray(biomass)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return biomass.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return biomass.filter((item) => withYear(item) >= 2025);
  return biomass;
}

function isBiomassAllZero(data: unknown[]): boolean {
  if (!data?.length) return true;
  const keys = ['biomass', 'ofl', 'abc', 'landings'];
  return data.every((d) => {
    const obj = d as Record<string, unknown>;
    return keys.every((key) => obj[key] === 0 || obj[key] === null || obj[key] === undefined);
  });
}

export default function TradeDeficit(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<TradeDeficitData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { biomass?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredBiomass = useMemo(
    () => filterBiomassByYear(pageTimeSeriesData?.biomass, selectedYear),
    [pageTimeSeriesData?.biomass, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenOflAbcModal = injectionData.onOpenOflAbcModal ?? (canOpenModal ? () => setShowModal(true) : undefined);

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openOflAbcModal', handler);
    return () => window.removeEventListener('openOflAbcModal', handler);
  }, []);

  const displayStockName = (pageStockName === 'all stocks' || !pageStockName) ? 'All Stocks' : pageStockName;
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;

  return (
    <div className="trade-deficit insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Trade Deficit</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: DESCRIPTION }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.tradeDeficit}
        currentValue={insights.tradeDeficit}
        statusQuoContent={
          <>
            {formatCurrencySigFig(statusQuoDisplay.tradeDeficit)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(statusQuoDisplay.tradeDeficitUncertainty, false)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatCurrencySigFig(insights.tradeDeficit)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(insights.tradeDeficitUncertainty, false)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenOflAbcModal}
        clickable={Boolean(onOpenOflAbcModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      {showModal &&
        createPortal(
          <Modal
            title={`OFL/ABC/LANDINGS: ${displayStockName}`}
            onClose={handleCloseModal}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '90vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            {pageLoading ? (
              <p className="noscroll-loading-message">Loading data...</p>
            ) : pageError ? (
              <p className="noscroll-error-message">{pageError}</p>
            ) : !filteredBiomass.length || isBiomassAllZero(filteredBiomass) ? (
              <p className="noscroll-no-data-message">Data deficient</p>
            ) : (
              <BiomassOflAbcChart
                {...({ data: filteredBiomass, maxHeight: 400 } as React.ComponentProps<typeof BiomassOflAbcChart>)}
              />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
